import type { RolePermissionsResponse } from "@credit-acceptance/step6-bff-client";

import type { SectionCapability, WorkbenchRoleCapabilities, WorkbenchSectionId } from "./types";

/**
 * Base `programRoleSet` marker each Step6 section is gated by (matrix §2a).
 * A role's flat `permissions[]` array is expected to carry these bare
 * strings (section visible) plus `${marker}_W` (write) / `${marker}_R` (forced
 * read-only) variants where applicable.
 */
const TAB_MARKERS: Record<WorkbenchSectionId, string> = {
  dealStatus: "STATUS",
  financial: "FINANCIAL",
  vehicle: "VEHICLE",
  driver: "DRIVER",
  secondSigner: "SECONDSIGNER",
  internal: "INTERNAL",
};

/**
 * Fail-safe verdict used for a section when the source response can't be
 * trusted (unknown role, empty/missing `permissions`, malformed payload).
 * Matches the doc's "fail-safe default: read-only if unknown" principle —
 * here that means hidden, since visibility can't be assumed either.
 */
const DENY_ALL_CAPABILITY: SectionCapability = {
  visible: false,
  writable: false,
  forcedReadOnly: true,
};

/**
 * Extra `_R` markers that also force a section read-only. Per the Financial tab
 * spec (§3.3), a read-only marker on Driver, Second Signer or Vehicle locks
 * Financial too. The legacy cross-coupling is otherwise NOT reproduced: those
 * sections stay scoped to their own `_R`, and `FINANCIAL_R` does not lock them.
 */
const CROSS_LOCK_MARKERS: Partial<Record<WorkbenchSectionId, readonly string[]>> = {
  financial: ["DRIVER_R", "SECONDSIGNER_R", "VEHICLE_R"],
};

/**
 * Derives one section's visible/writable/forcedReadOnly verdict from a
 * permission set and its base marker. `_R` is scoped to the section it names,
 * plus the Financial-only cross-lock in {@link CROSS_LOCK_MARKERS}; see matrix
 * §2a/§6.4 and the Financial access spec §3.3.
 * @param {ReadonlySet<string>} permissionSet - Upper-cased permission strings for the role.
 * @param {string} marker - The section's base marker, e.g. `"VEHICLE"`.
 * @param {WorkbenchSectionId} sectionId - The section being derived, used to look up cross-lock markers.
 * @returns {SectionCapability} The section's parsed capability.
 */
function deriveSectionCapability(
  permissionSet: ReadonlySet<string>,
  marker: string,
  sectionId: WorkbenchSectionId
): SectionCapability {
  const crossLocked = (CROSS_LOCK_MARKERS[sectionId] ?? []).some((crossMarker) => permissionSet.has(crossMarker));
  const forcedReadOnly = permissionSet.has(`${marker}_R`) || crossLocked;
  return {
    visible: permissionSet.has(marker),
    writable: permissionSet.has(`${marker}_W`) && !forcedReadOnly,
    forcedReadOnly,
  };
}

/**
 * Converts the raw `RolePermissionsResponse` from
 * `ProgramRoleCapabilitiesApi.getRolePermissions` into the Workbench's
 * per-section capability shape. Permission strings unrelated to Step6 (e.g.
 * `DEALERPROFILE_*`, `VSCSETUP*`, `USERSETUP_W`) belong to other CAPS/DMS
 * modules and are ignored here.
 * @param {RolePermissionsResponse | undefined} response - The raw BFF response, or undefined on fetch failure.
 * @returns {WorkbenchRoleCapabilities} The parsed, Workbench-usable capabilities. Fails safe (every section hidden/read-only) when `response` is undefined or carries no permissions.
 */
export function parseRolePermissions(response: RolePermissionsResponse | undefined): WorkbenchRoleCapabilities {
  const roleName = response?.roleName ?? "";
  const userType = response?.userType ?? "";
  const permissionSet = new Set((response?.permissions ?? []).map((permission) => permission.toUpperCase()));

  const sections = Object.fromEntries(
    (Object.entries(TAB_MARKERS) as [WorkbenchSectionId, string][]).map(([sectionId, marker]) => [
      sectionId,
      permissionSet.size === 0 ? DENY_ALL_CAPABILITY : deriveSectionCapability(permissionSet, marker, sectionId),
    ])
  ) as Record<WorkbenchSectionId, SectionCapability>;

  return {
    roleName,
    userType,
    isCacUser: userType.toUpperCase() === "CAC",
    sections,
    canExit: permissionSet.has("EXIT"),
  };
}
